import React, { useState, useEffect } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { updateProfile, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../lib/firebase';
import {
    validatePasswordRequirements,
    calculatePasswordStrength,
    checkPasswordHistory,
    hashPasswordForHistory,
    generateSecurePassword
} from '../../utils/passwordUtils';
import { Eye, EyeOff, Lock, User, Building, Shield, AlertCircle, CheckCircle, Settings as SettingsIcon, Mail, ExternalLink } from 'lucide-react';
import { LEGAL_URLS } from '../../config/legalUrls';
import { PageHeader } from '../../components/common/PageHeader';
import { useCapability } from '../../hooks/useCapability';
import { useCookieConsent } from '../../hooks/useCookieConsent';
import { ConnectedAccountsContent } from './ConnectedAccountsPage';
import TwoFactorSecurityCard from './TwoFactorSecurityCard';

interface OutletContextType {
  userData: any;
  companyData: any;
}

const SettingsPage: React.FC = () => {
    const { userData, companyData } = useOutletContext<OutletContextType>();
    const [searchParams, setSearchParams] = useSearchParams();
    const { consent, grantAnalytics, revokeAnalytics, grantAI, revokeAI } = useCookieConsent();

    // Company Settings State
    const [companyName, setCompanyName] = useState('');
    const [companyWebsite, setCompanyWebsite] = useState('');
    const [companyPhone, setCompanyPhone] = useState('');
    const [companyAddress, setCompanyAddress] = useState('');
    const [companyLoading, setCompanyLoading] = useState(false);
    const [companyMessage, setCompanyMessage] = useState('');
    const [companyError, setCompanyError] = useState('');

    // Profile Settings State
    const [displayName, setDisplayName] = useState('');
    const [mobileNumber, setMobileNumber] = useState('');
    const [profileLoading, setProfileLoading] = useState(false);
    const [profileMessage, setProfileMessage] = useState('');
    const [profileError, setProfileError] = useState('');

    // Password Settings State
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [passwordMessage, setPasswordMessage] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState<any>(null);
    const [passwordRequirements, setPasswordRequirements] = useState<any>(null);
    const [passwordHistory, setPasswordHistory] = useState<any[]>([]);

    // Tab State for better organization — supports ?tab= URL parameter for deep linking
    const initialTab = searchParams.get('tab') || 'profile';
    const [activeTab, setActiveTab] = useState(initialTab);

    // Sync tab state with URL when ?tab= changes (e.g., navigated from banner)
    useEffect(() => {
        const tabParam = searchParams.get('tab');
        if (tabParam && tabParam !== activeTab) {
            setActiveTab(tabParam);
        }
    }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (companyData) {
            setCompanyName(companyData.companyName || '');
            setCompanyWebsite(companyData.website || '');
            setCompanyPhone(companyData.phone || '');
            setCompanyAddress(companyData.address || '');
        }
        if (userData) {
            setDisplayName(userData.displayName || '');
            setMobileNumber(userData.phoneNumber || '');
        }
    }, [companyData, userData]);

    useEffect(() => {
        // Load password history when component mounts
        const loadPasswordHistory = async () => {
            if (userData?.uid) {
                try {
                    const userDoc = await getDoc(doc(db, 'users', userData.uid));
                    const history = userDoc.data()?.passwordHistory || [];
                    setPasswordHistory(history);
                } catch (error: any) {
                  // console.error('Error loading password history:', error);
                }
            }
        };
        loadPasswordHistory();
    }, [userData]);

    useEffect(() => {
        // Real-time password validation
        if (newPassword) {
            const requirements = validatePasswordRequirements(newPassword);
            const strength = calculatePasswordStrength(newPassword);
            setPasswordRequirements(requirements);
            setPasswordStrength(strength);
        } else {
            setPasswordRequirements(null);
            setPasswordStrength(null);
        }
    }, [newPassword]);

    const handleCompanySubmit = async (e: any) => {
        e.preventDefault();
        if (!companyData?.id) return;

        if (!hasCompanyDetailsChanged()) return;

        setCompanyLoading(true);
        setCompanyMessage('');
        setCompanyError('');
        try {
            const companyDocRef = doc(db, 'companies', companyData.id);
            await updateDoc(companyDocRef, { 
                companyName,
                website: companyWebsite,
                phone: companyPhone,
                address: companyAddress,
            });
            setCompanyMessage('Company details updated successfully!');
            setTimeout(() => setCompanyMessage(''), 5000);
        } catch (err: any) {
            setCompanyError(err.message);
        } finally {
            setCompanyLoading(false);
        }
    };

    const handleProfileSubmit = async (e: any) => {
        e.preventDefault();
        if (!auth.currentUser) return;

        if (!hasProfileDetailsChanged()) return;

        setProfileLoading(true);
        setProfileMessage('');
        setProfileError('');
        try {
            if (displayName !== (userData?.displayName || '')) {
                await updateProfile(auth.currentUser, { displayName });
            }

            const userDocRef = doc(db, 'users', userData.uid);
            await updateDoc(userDocRef, {
                displayName,
                phoneNumber: mobileNumber
            });

            setProfileMessage('Your profile has been updated!');
            setTimeout(() => setProfileMessage(''), 5000);
        } catch (err: any) {
            setProfileError(err.message);
        } finally {
            setProfileLoading(false);
        }
    };

    const handlePasswordSubmit = async (e: any) => {
        e.preventDefault();
        setPasswordError('');
        setPasswordMessage('');

        // Validation checks
        if (newPassword !== confirmPassword) {
            setPasswordError('Passwords do not match');
            return;
        }

        if (!passwordStrength?.meetsRequirements) {
            setPasswordError('Password does not meet security requirements');
            return;
        }

        // Check password history
        const isReused = await checkPasswordHistory(newPassword, passwordHistory);
        if (isReused) {
            setPasswordError('This password was recently used. Please choose a different password.');
            return;
        }

        setPasswordLoading(true);
        try {
            // Re-authenticate user
            if (!auth.currentUser?.email) {
                setPasswordError('User not found');
                setPasswordLoading(false);
                return;
            }

            const credential = EmailAuthProvider.credential(
                auth.currentUser.email,
                currentPassword
            );
            await reauthenticateWithCredential(auth.currentUser, credential);

            // Update password
            await updatePassword(auth.currentUser, newPassword);

            // Update password history in Firestore
            const newPasswordHash = await hashPasswordForHistory(newPassword);
            const userDocRef = doc(db, 'users', userData.uid);
            
            // Keep only last 5 password hashes
            const updatedHistory = [...passwordHistory, newPasswordHash].slice(-5);
            await updateDoc(userDocRef, {
                passwordHistory: updatedHistory,
                passwordLastChanged: new Date().toISOString()
            });

            setPasswordMessage('Password updated successfully!');
            setPasswordHistory(updatedHistory);
            
            // Clear form
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            
            setTimeout(() => setPasswordMessage(''), 5000);
        } catch (err: any) {
            if (err.code === 'auth/wrong-password') {
                setPasswordError('Current password is incorrect');
            } else if (err.code === 'auth/weak-password') {
                setPasswordError('Password is too weak');
            } else {
                setPasswordError(err.message || 'Failed to update password');
            }
        } finally {
            setPasswordLoading(false);
        }
    };

    const generatePassword = () => {
        const generated = generateSecurePassword(16);
        setNewPassword(generated);
        setConfirmPassword(generated);
    };

    // Capability gate for the Connected Accounts tab (2026-05-19). Mirrors the
    // legacyAllow on the corresponding RequireCapability guard in AppRoutes.tsx
    // so kill-switch-OFF tenants stay byte-identical with the pre-PR universal
    // tab access. Marketing is the only role removed.
    const canManageConnectedAccounts = useCapability('manage_connected_accounts', [
        'super_admin', 'administrator', 'office_admin', 'broker', 'broker_assistant', 'credit_analyst',
    ]);

    const tabs = [
        { id: 'profile', label: 'Profile', icon: User, color: 'from-blue-500 to-purple-500' },
        { id: 'security', label: 'Security', icon: Shield, color: 'from-green-500 to-teal-500' },
        ...(canManageConnectedAccounts ? [
            { id: 'connected-accounts', label: 'Connected Accounts', icon: Mail, color: 'from-red-500 to-pink-500' }
        ] : []),
        { id: 'privacy', label: 'Privacy & Consent', icon: Lock, color: 'from-indigo-500 to-blue-500' },
        ...((['admin', 'super_admin'].includes(userData?.role)) ?
            [
                { id: 'company', label: 'Company', icon: Building, color: 'from-orange-500 to-red-500' }
            ] : [])
    ];

    const hasCompanyDetailsChanged = () => {
        if (!companyData) return false;
        return companyName !== (companyData.companyName || '') ||
               companyWebsite !== (companyData.website || '') ||
               companyPhone !== (companyData.phone || '') ||
               companyAddress !== (companyData.address || '');
    };

    const hasProfileDetailsChanged = () => {
        if (!userData) return false;
        return displayName !== (userData.displayName || '') ||
               mobileNumber !== (userData.phoneNumber || '');
    };

    return (
        <div className="space-y-6">
            <div className="max-w-6xl mx-auto space-y-6">
                {/* Page Header */}
                <PageHeader
                    icon={SettingsIcon}
                    title="Settings"
                    subtitle="Manage your profile, security, and company details"
                />

                {/* Tab Navigation */}
                <div className="flex space-x-2 mb-8">
                    {tabs.map((tab: any) => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => {
                                    setActiveTab(tab.id);
                                    setSearchParams(tab.id === 'profile' ? {} : { tab: tab.id });
                                }}
                                className={`flex items-center space-x-2 px-6 py-3 rounded-lg transition-all duration-200 ${
                                    activeTab === tab.id
                                        ? `bg-gradient-to-r ${tab.color} text-white shadow-lg transform scale-105`
                                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
                                }`}
                            >
                                <Icon size={20} />
                                <span className="font-medium">{tab.label}</span>
                            </button>
                        );
                    })}
                </div>

                {/* Profile Tab */}
                {activeTab === 'profile' && (
                    <div className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-2xl shadow-xl border border-gray-700">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center mr-4">
                                <User size={24} />
                            </div>
                            <h3 className="text-2xl font-bold">Your Profile</h3>
                        </div>

                        {profileError && (
                            <div className="bg-red-900/50 border border-red-500 text-red-200 p-4 rounded-lg mb-6 flex items-center">
                                <AlertCircle className="mr-2" size={20} />
                                {profileError}
                            </div>
                        )}
                        {profileMessage && (
                            <div className="bg-green-900/50 border border-green-500 text-green-200 p-4 rounded-lg mb-6 flex items-center">
                                <CheckCircle className="mr-2" size={20} />
                                {profileMessage}
                            </div>
                        )}

                        <form onSubmit={handleProfileSubmit}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                <div>
                                    <label htmlFor="display-name" className="block mb-3 text-sm font-medium text-gray-300">
                                        Full Name
                                    </label>
                                    <input
                                        type="text"
                                        id="display-name"
                                        value={displayName}
                                        onChange={(e) => setDisplayName(e.target.value)}
                                        className="w-full bg-gray-700/50 border border-gray-600 text-white text-sm rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent block p-3 transition-all duration-200"
                                        required
                                    />
                                </div>
                                <div>
                                    <label htmlFor="email" className="block mb-3 text-sm font-medium text-gray-300">
                                        Email Address
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        value={userData?.email || ''}
                                        readOnly
                                        className="w-full bg-gray-900/50 border border-gray-700 text-gray-400 text-sm rounded-lg block p-3 cursor-not-allowed"
                                    />
                                </div>
                            </div>
                            <div className="mb-6">
                                <label htmlFor="mobile-number" className="block mb-3 text-sm font-medium text-gray-300">
                                    Mobile Phone Number
                                </label>
                                <input
                                    type="tel"
                                    id="mobile-number"
                                    value={mobileNumber}
                                    onChange={(e) => setMobileNumber(e.target.value)}
                                    className="w-full bg-gray-700/50 border border-gray-600 text-white text-sm rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent block p-3 transition-all duration-200"
                                />
                            </div>
                            <button 
                                type="submit" 
                                disabled={profileLoading || !hasProfileDetailsChanged()} 
                                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white p-3 rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02]"
                            >
                                {profileLoading ? 'Saving...' : 'Save Profile Changes'}
                            </button>
                        </form>
                    </div>
                )}

                {/* Security Tab */}
                {activeTab === 'security' && (
                    <div className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-2xl shadow-xl border border-gray-700">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-green-500 to-teal-500 flex items-center justify-center mr-4">
                                <Lock size={24} />
                            </div>
                            <h3 className="text-2xl font-bold">Security Settings</h3>
                        </div>

                        {passwordError && (
                            <div className="bg-red-900/50 border border-red-500 text-red-200 p-4 rounded-lg mb-6 flex items-center">
                                <AlertCircle className="mr-2" size={20} />
                                {passwordError}
                            </div>
                        )}
                        {passwordMessage && (
                            <div className="bg-green-900/50 border border-green-500 text-green-200 p-4 rounded-lg mb-6 flex items-center">
                                <CheckCircle className="mr-2" size={20} />
                                {passwordMessage}
                            </div>
                        )}

                        <form onSubmit={handlePasswordSubmit} className="space-y-6">
                            {/* Current Password */}
                            <div>
                                <label htmlFor="current-password" className="block mb-3 text-sm font-medium text-gray-300">
                                    Current Password
                                </label>
                                <div className="relative">
                                    <input
                                        type={showCurrentPassword ? "text" : "password"}
                                        id="current-password"
                                        value={currentPassword}
                                        onChange={(e: any) => setCurrentPassword(e.target.value)}
                                        className="w-full bg-gray-700/50 border border-gray-600 text-white text-sm rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent block p-3 pr-10 transition-all duration-200"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white"
                                    >
                                        {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                    </button>
                                </div>
                            </div>

                            {/* New Password */}
                            <div>
                                <label htmlFor="new-password" className="block mb-3 text-sm font-medium text-gray-300">
                                    New Password
                                </label>
                                <div className="relative">
                                    <input
                                        type={showNewPassword ? "text" : "password"}
                                        id="new-password"
                                        value={newPassword}
                                        onChange={(e: any) => setNewPassword(e.target.value)}
                                        className="w-full bg-gray-700/50 border border-gray-600 text-white text-sm rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent block p-3 pr-10 transition-all duration-200"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white"
                                    >
                                        {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                    </button>
                                </div>

                                {/* Password Strength Meter */}
                                {passwordStrength && (
                                    <div className="mt-3 space-y-2">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs text-gray-400">Password Strength</span>
                                            <span className={`text-xs font-medium ${
                                                passwordStrength.strength.level === 0 ? 'text-red-400' :
                                                passwordStrength.strength.level === 1 ? 'text-orange-400' :
                                                passwordStrength.strength.level === 2 ? 'text-yellow-400' :
                                                passwordStrength.strength.level === 3 ? 'text-green-400' :
                                                'text-emerald-400'
                                            }`}>
                                                {passwordStrength.strength.label}
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-700 rounded-full h-2 overflow-hidden">
                                            <div 
                                                className={`h-full transition-all duration-300 bg-gradient-to-r ${passwordStrength.strength.gradient}`}
                                                style={{ width: `${passwordStrength.percentage}%` }}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Password Requirements */}
                                {passwordRequirements && (
                                    <div className="mt-3 space-y-1">
                                        {Object.entries(passwordRequirements.requirements).map(([key, req]: [string, any]) => (
                                            <div key={key} className="flex items-center text-xs">
                                                <span className={`mr-2 ${req.met ? 'text-green-400' : 'text-gray-500'}`}>
                                                    {req.met ? '✓' : '○'}
                                                </span>
                                                <span className={req.met ? 'text-gray-300' : 'text-gray-500'}>
                                                    {req.message}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <button
                                    type="button"
                                    onClick={generatePassword}
                                    className="mt-2 text-xs text-purple-400 hover:text-purple-300 transition-colors"
                                >
                                    Generate secure password
                                </button>
                            </div>

                            {/* Confirm Password */}
                            <div>
                                <label htmlFor="confirm-password" className="block mb-3 text-sm font-medium text-gray-300">
                                    Confirm New Password
                                </label>
                                <div className="relative">
                                    <input
                                        type={showConfirmPassword ? "text" : "password"}
                                        id="confirm-password"
                                        value={confirmPassword}
                                        onChange={(e: any) => setConfirmPassword(e.target.value)}
                                        className="w-full bg-gray-700/50 border border-gray-600 text-white text-sm rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent block p-3 pr-10 transition-all duration-200"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white"
                                    >
                                        {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                    </button>
                                </div>
                                {confirmPassword && newPassword !== confirmPassword && (
                                    <p className="mt-1 text-xs text-red-400">Passwords do not match</p>
                                )}
                            </div>

                            <button 
                                type="submit" 
                                disabled={passwordLoading || !passwordStrength?.meetsRequirements || newPassword !== confirmPassword} 
                                className="w-full bg-gradient-to-r from-green-600 to-teal-600 text-white p-3 rounded-lg font-medium hover:from-green-700 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02]"
                            >
                                {passwordLoading ? 'Updating Password...' : 'Update Password'}
                            </button>
                        </form>

                        {/*
                            Two-Factor Authentication. Extracted to its own component and
                            moved OFF the legacy `claims?.mfaEnabled` claim, which native
                            GCIP enrolment never sets: see TwoFactorSecurityCard.tsx.
                        */}
                        <TwoFactorSecurityCard />
                    </div>
                )}

                {/* Privacy & Consent Tab */}
                {activeTab === 'privacy' && (
                    <div className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-2xl shadow-xl border border-gray-700">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-indigo-500 to-blue-500 flex items-center justify-center mr-4">
                                <Lock size={24} />
                            </div>
                            <h3 className="text-2xl font-bold">Privacy & Consent</h3>
                        </div>

                        <p className="text-gray-400 text-sm mb-6">
                            Control how Valutten collects and processes your data. Changes take effect immediately.
                        </p>

                        {/* Analytics Toggle */}
                        <div className="space-y-4">
                            <div className="flex items-center justify-between p-4 bg-gray-700/30 rounded-lg border border-gray-600">
                                <div className="flex-1 mr-4">
                                    <h4 className="text-base font-medium text-white">Product Analytics</h4>
                                    <p className="text-sm text-gray-400 mt-1">
                                        Allow anonymous usage analytics via PostHog. No financial data is tracked.
                                        Helps us improve the product.
                                    </p>
                                </div>
                                <button
                                    onClick={() => consent.analytics ? revokeAnalytics() : grantAnalytics()}
                                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 ${
                                        consent.analytics ? 'bg-blue-600' : 'bg-gray-600'
                                    }`}
                                    role="switch"
                                    aria-checked={consent.analytics}
                                    aria-label="Toggle product analytics"
                                >
                                    <span
                                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                            consent.analytics ? 'translate-x-5' : 'translate-x-0'
                                        }`}
                                    />
                                </button>
                            </div>

                            {/* AI Processing Toggle */}
                            <div className="flex items-center justify-between p-4 bg-gray-700/30 rounded-lg border border-gray-600">
                                <div className="flex-1 mr-4">
                                    <h4 className="text-base font-medium text-white">AI-Powered Insights</h4>
                                    <p className="text-sm text-gray-400 mt-1">
                                        Allow AI-powered features using Anthropic's Claude. Personal identifiers
                                        are tokenised before processing.
                                    </p>
                                </div>
                                <button
                                    onClick={() => consent.aiProcessing ? revokeAI() : grantAI()}
                                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 ${
                                        consent.aiProcessing ? 'bg-blue-600' : 'bg-gray-600'
                                    }`}
                                    role="switch"
                                    aria-checked={consent.aiProcessing}
                                    aria-label="Toggle AI processing"
                                >
                                    <span
                                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                            consent.aiProcessing ? 'translate-x-5' : 'translate-x-0'
                                        }`}
                                    />
                                </button>
                            </div>
                        </div>

                        {/* Current Status */}
                        <div className="mt-6 p-4 bg-gray-700/30 rounded-lg border border-gray-600">
                            <h4 className="text-sm font-medium text-gray-300 mb-3">Current Status</h4>
                            <div className="flex flex-wrap gap-3">
                                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                                    consent.analytics
                                        ? 'bg-green-500/10 text-green-400'
                                        : 'bg-gray-600/50 text-gray-400'
                                }`}>
                                    <span className={`w-1.5 h-1.5 rounded-full ${consent.analytics ? 'bg-green-400' : 'bg-gray-500'}`} />
                                    Analytics {consent.analytics ? 'enabled' : 'disabled'}
                                </span>
                                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                                    consent.aiProcessing
                                        ? 'bg-green-500/10 text-green-400'
                                        : 'bg-gray-600/50 text-gray-400'
                                }`}>
                                    <span className={`w-1.5 h-1.5 rounded-full ${consent.aiProcessing ? 'bg-green-400' : 'bg-gray-500'}`} />
                                    AI features {consent.aiProcessing ? 'enabled' : 'disabled'}
                                </span>
                            </div>
                        </div>

                        {/* Links */}
                        <div className="mt-6 flex flex-wrap gap-4">
                            <a
                                href={LEGAL_URLS.privacy}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                            >
                                <ExternalLink size={14} />
                                Privacy Policy
                            </a>
                            <a
                                href={LEGAL_URLS.cookies}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                            >
                                <ExternalLink size={14} />
                                Cookie & Storage Notice
                            </a>
                        </div>
                    </div>
                )}

                {/* Connected Accounts Tab — defence in depth: tab is filtered above
                    via canManageConnectedAccounts; this guard also covers deep-linked
                    URL params like ?tab=connected-accounts for an unauthorised role. */}
                {activeTab === 'connected-accounts' && canManageConnectedAccounts && (
                    <ConnectedAccountsContent companyData={companyData} userData={userData} />
                )}

                {/* Company Tab */}
                {activeTab === 'company' && ['admin', 'super_admin'].includes(userData?.role) && (
                    <div className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-2xl shadow-xl border border-gray-700">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center mr-4">
                                <Building size={24} />
                            </div>
                            <h3 className="text-2xl font-bold">Company Details</h3>
                        </div>

                        {companyError && (
                            <div className="bg-red-900/50 border border-red-500 text-red-200 p-4 rounded-lg mb-6 flex items-center">
                                <AlertCircle className="mr-2" size={20} />
                                {companyError}
                            </div>
                        )}
                        {companyMessage && (
                            <div className="bg-green-900/50 border border-green-500 text-green-200 p-4 rounded-lg mb-6 flex items-center">
                                <CheckCircle className="mr-2" size={20} />
                                {companyMessage}
                            </div>
                        )}

                        <form onSubmit={handleCompanySubmit}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                <div>
                                    <label htmlFor="company-name" className="block mb-3 text-sm font-medium text-gray-300">
                                        Company Name
                                    </label>
                                    <input
                                        type="text"
                                        id="company-name"
                                        value={companyName}
                                        onChange={(e) => setCompanyName(e.target.value)}
                                        className="w-full bg-gray-700/50 border border-gray-600 text-white text-sm rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent block p-3 transition-all duration-200"
                                        required
                                    />
                                </div>
                                <div>
                                    <label htmlFor="company-website" className="block mb-3 text-sm font-medium text-gray-300">
                                        Website
                                    </label>
                                    <input
                                        type="url"
                                        id="company-website"
                                        value={companyWebsite}
                                        onChange={(e) => setCompanyWebsite(e.target.value)}
                                        className="w-full bg-gray-700/50 border border-gray-600 text-white text-sm rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent block p-3 transition-all duration-200"
                                        placeholder="https://example.com"
                                    />
                                </div>
                            </div>
                            <div className="mb-6">
                                <label htmlFor="company-phone" className="block mb-3 text-sm font-medium text-gray-300">
                                    Phone Number
                                </label>
                                <input
                                    type="tel"
                                    id="company-phone"
                                    value={companyPhone}
                                    onChange={(e) => setCompanyPhone(e.target.value)}
                                    className="w-full bg-gray-700/50 border border-gray-600 text-white text-sm rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent block p-3 transition-all duration-200"
                                />
                            </div>
                            <div className="mb-6">
                                <label htmlFor="company-address" className="block mb-3 text-sm font-medium text-gray-300">
                                    Company Address
                                </label>
                                <textarea
                                    id="company-address"
                                    value={companyAddress}
                                    onChange={(e) => setCompanyAddress(e.target.value)}
                                    rows={4}
                                    className="w-full bg-gray-700/50 border border-gray-600 text-white text-sm rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent block p-3 transition-all duration-200"
                                />
                            </div>
                            <button 
                                type="submit" 
                                disabled={companyLoading || !hasCompanyDetailsChanged()} 
                                className="w-full bg-gradient-to-r from-orange-600 to-red-600 text-white p-3 rounded-lg font-medium hover:from-orange-700 hover:to-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02]"
                            >
                                {companyLoading ? 'Saving...' : 'Save Company Changes'}
                            </button>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SettingsPage;